import * as R from 'remeda'
import {
  AttendeeId,
  MeetingTypeRef,
  UserAttendeeId,
  MeetingAttendees,
  AvailabilityChecks,
  MeetingAttemptResponseV2,
  RoundRobinAvailableAttendees,
  UsersWithLimit,
  ReserveTimeSlotRequestV3,
  ReserveMeetingAttemptRequestV2,
  ReservationUsers,
  AttendeeFromDistribution,
  ReserveMeetingAttemptRequest,
  ReserveTimeSlotRequest,
} from '@chilipiper/api-client/src/user-availability'
import {
  GroupSchedulingLink,
  OptionMeetingTypeOverride,
  ArrayAlwaysInvitedUser,
} from '@chilipiper/api-client/src/team-scheduling'
import { MeetingLocations } from '@chilipiper/api-client/src/meeting-types'
import { SelectRouteResponse } from '@chilipiper/api-client/src/handoff'
import { isDefined } from 'remeda'
import { MeetingLimitSource } from '../meeting-host-limit'
import {
  WithDistribution,
  OwnershipLinkRoutingResult,
  isWithDistribution,
  isMixedResults,
  isOwnerFromSimpleRule,
  isOwner,
  isDistributionUsers,
  LinkResource,
  isStrictDistribution,
  isFlexibleDistribution,
} from '../meeting-attempt/types'
import { AvailabilityOptions, BuildBaseRequest, BaseRequest } from './types'

export const isInternalUser = (attendeeId: AttendeeId): attendeeId is UserAttendeeId =>
  attendeeId.type === 'UserAttendeeId'

export const buildRequestBase = ({
  intervalDuration,
  intervalStartsAt,
  meetingTypeId,
  meetingTypeVersion = new Date().toISOString(),
  afterBufferDurationOverride,
  beforeBufferDurationOverride,
  meetingDurationOverride,
}: BuildBaseRequest): BaseRequest => {
  return {
    interval: {
      duration: intervalDuration,
      startsAt: intervalStartsAt,
    },
    meetingTypeRef: {
      id: meetingTypeId,
      timestamp: meetingTypeVersion,
    },
    meetingTypeOverride: {
      meetingDurationOverride: meetingDurationOverride,
      beforeBufferDurationOverride: beforeBufferDurationOverride,
      afterBufferDurationOverride: afterBufferDurationOverride,
    },
  }
}

export const getUserIdsFromAvailabilityOptions = (
  availabilityOptions: AvailabilityOptions,
  additionalRequiredAttendees: string[] = []
) => {
  if ('assigneeIds' in availabilityOptions) {
    return availabilityOptions.assigneeIds.concat(additionalRequiredAttendees)
  }

  if ('rankedAssigneeIds' in availabilityOptions) {
    return availabilityOptions.rankedAssigneeIds.concat(additionalRequiredAttendees)
  }

  if ('distributionAttendees' in availabilityOptions) {
    const distributionUserIds = availabilityOptions.distributionAttendees.flatMap(
      item => item.userIds
    )
    return [
      ...distributionUserIds,
      ...availabilityOptions.ownershipAndUsersAttendees,
      ...additionalRequiredAttendees,
    ]
  }

  return availabilityOptions.userIds.concat(additionalRequiredAttendees)
}

export const getRoundRobinAvailableAttendeesFromOwnershipOrRoundRobinRoutingResult = (
  ownershipRoutingResult?: OwnershipLinkRoutingResult
): RoundRobinAvailableAttendees[] => {
  if (!ownershipRoutingResult) {
    return []
  }

  if (isWithDistribution(ownershipRoutingResult)) {
    const roundRobinAttendees: RoundRobinAvailableAttendees[] = []

    ownershipRoutingResult.distributions.forEach(dist => {
      if (isStrictDistribution(dist)) {
        const firstUserId = dist.users?.[0]
        if (firstUserId) {
          roundRobinAttendees.push({
            type: 'StrictRoundRobin',
            distributionId: dist.id,
            userId: firstUserId,
          })
        }
      } else {
        roundRobinAttendees.push({
          type: 'FlexibleRoundRobin',
          distributionId: dist.id,
          userIds: dist.users || [],
        })
      }
    })

    return roundRobinAttendees
  }

  if (isMixedResults(ownershipRoutingResult)) {
    const roundRobinAttendees: RoundRobinAvailableAttendees[] = []

    ownershipRoutingResult.result.forEach(item => {
      if (isDistributionUsers(item)) {
        if (isStrictDistribution(item)) {
          const firstUserId = item.users?.[0]
          if (firstUserId) {
            roundRobinAttendees.push({
              type: 'StrictRoundRobin',
              distributionId: item.id,
              userId: firstUserId,
            })
          }
        } else {
          roundRobinAttendees.push({
            type: 'FlexibleRoundRobin',
            distributionId: item.id,
            userIds: item.users || [],
          })
        }
      }
    })

    return roundRobinAttendees
  }

  return []
}

export type MeetingTypeOverride = OptionMeetingTypeOverride & {
  duration?: string
  locations?: MeetingLocations
}

type ReserveTimeSlotProps = {
  users: ReservationUsers
  slotStartsAt: string
  meetingTypeOverride?: Partial<MeetingTypeOverride>
  meetingTypeRef: MeetingTypeRef
  availabilityChecks?: AvailabilityChecks
}

/** @deprecated Use buildReserveTimeSlotPayload instead */
export const buildReserveTimeSlotPayloadDeprecated = ({
  userIds,
  slotStartsAt,
  meetingTypeOverride,
  meetingTypeRef,
  availabilityChecks,
  meetingLimit,
}: Omit<ReserveTimeSlotProps, 'users'> & { userIds: string[]; meetingLimit?: MeetingLimitSource }):
  | ReserveTimeSlotRequest
  | ReserveMeetingAttemptRequest => {
  const meetingTypeOverrideProps = getReservationMeetingTypeOverride(meetingTypeOverride)

  return {
    meetingTypeRef,
    slotStartsAt,
    meetingLimit: meetingLimit ?? {
      type: 'NoMeetingLimit',
    },
    userIds,
    durationOverride: meetingTypeOverrideProps.durationOverride,
    afterBufferDurationOverride: meetingTypeOverrideProps.afterBufferDurationOverride,
    beforeBufferDurationOverride: meetingTypeOverrideProps.beforeBufferDurationOverride,
    availabilityChecks: availabilityChecks ?? {
      type: 'All',
    },
  }
}

export const buildReserveTimeSlotPayload = ({
  users,
  slotStartsAt,
  meetingTypeOverride,
  meetingTypeRef,
  availabilityChecks,
}: ReserveTimeSlotProps): ReserveTimeSlotRequestV3 | ReserveMeetingAttemptRequestV2 => {
  const meetingTypeOverrideProps = getReservationMeetingTypeOverride(meetingTypeOverride)

  return {
    meetingTypeRef,
    slotStartsAt,
    users,
    durationOverride: meetingTypeOverrideProps.durationOverride,
    afterBufferDurationOverride: meetingTypeOverrideProps.afterBufferDurationOverride,
    beforeBufferDurationOverride: meetingTypeOverrideProps.beforeBufferDurationOverride,
    availabilityChecks: availabilityChecks ?? {
      type: 'All',
    },
  }
}

export const parseReservationUsersWithLimit = (
  userIds: string[],
  meetingLimit?: MeetingLimitSource
): UsersWithLimit => {
  const limit = !meetingLimit
    ? {
        type: 'NoMeetingLimit' as const,
      }
    : meetingLimit

  return {
    userIds,
    limit,
    type: 'UsersWithLimit',
  }
}

export const getReservationMeetingTypeOverride = (
  meetingTypeOverride?: Partial<MeetingTypeOverride>
) => {
  return {
    durationOverride: meetingTypeOverride?.duration,
    afterBufferDurationOverride:
      meetingTypeOverride?.buffers?.after.type === 'Change'
        ? meetingTypeOverride?.buffers.after.value
        : undefined,
    beforeBufferDurationOverride:
      meetingTypeOverride?.buffers?.before.type === 'Change'
        ? meetingTypeOverride?.buffers.before.value
        : undefined,
  }
}

export const getAdditionalRequiredAttendeesId = (attendees?: MeetingAttendees) => {
  if (!attendees || attendees.type === 'NoAdditionalAttendees') {
    return []
  }

  return attendees.attendees
    .filter(attendee => isInternalUser(attendee.id) && !attendee.optional)
    .map(attendee => (isInternalUser(attendee.id) ? attendee.id.userId : ''))
}

export const getAdditionalRequiredAttendeesWithFallback = ({
  meetingAttemptAttendees,
  alwaysInvitedUsers = [],
}: {
  meetingAttemptAttendees?: MeetingAttendees
  alwaysInvitedUsers?: ArrayAlwaysInvitedUser
}) => {
  const fallbackAttendees =
    alwaysInvitedUsers?.filter(user => !user.optional).map(user => user.userId) ?? []

  const meetingAttemptAdditionalRequiredAttendees =
    getAdditionalRequiredAttendeesId(meetingAttemptAttendees)

  return meetingAttemptAttendees ? meetingAttemptAdditionalRequiredAttendees : fallbackAttendees
}

const getUniqUserIdsList = (userIds: string[]) => Array.from(new Set(userIds))

export const getMeetingAttemptReservationUserIdsAndMeetingLimit = ({
  meetingAttempt,
  routing,
  roundRobinRoutingResult,
  ownershipRoutingResult,
  groupSchedulingLink,
  linkResource,
  meetingLimit = {
    type: 'NoMeetingLimit',
  },
}: {
  meetingAttempt: MeetingAttemptResponseV2
  routing?: SelectRouteResponse
  roundRobinRoutingResult?: WithDistribution
  ownershipRoutingResult?: OwnershipLinkRoutingResult
  groupSchedulingLink?: GroupSchedulingLink
  linkResource?: LinkResource
  meetingLimit?: MeetingLimitSource
}): ReservationUsers | undefined => {
  if (meetingAttempt && meetingAttempt?.type !== 'NotFound') {
    // Extract alwaysInvitedUsers from linkResource for fallback
    const alwaysInvitedUsers =
      linkResource &&
      'source' in linkResource &&
      linkResource.source &&
      'alwaysInvitedUsers' in linkResource.source
        ? linkResource.source.alwaysInvitedUsers
        : []

    const additionalAttendees = getAdditionalRequiredAttendeesWithFallback({
      meetingAttemptAttendees: meetingAttempt.attempt.additionalAttendees,
      alwaysInvitedUsers,
    })

    if (
      meetingAttempt.attempt.productFeature.type === 'AdminIndividualSchedulingLink' ||
      meetingAttempt.attempt.productFeature.type === 'PersonalIndividualSchedulingLink'
    ) {
      return {
        type: 'UsersWithLimit',
        limit: meetingLimit,
        userIds: getUniqUserIdsList([
          meetingAttempt.attempt.productFeature.hostId,
          ...additionalAttendees,
        ]),
      }
    }

    if (meetingAttempt.attempt.productFeature.type === 'HandoffRouting') {
      return getHandoffReservationUsers({
        routing,
        additionalAttendees,
        meetingLimit,
      })
    }

    if (
      meetingAttempt.attempt.productFeature.type === 'RoundRobinSchedulingLink' &&
      roundRobinRoutingResult
    ) {
      return getRoundRobinReservationUsers({
        roundRobinRoutingResult,
        additionalAttendees,
      })
    }

    if (
      meetingAttempt.attempt.productFeature.type === 'OwnershipSchedulingLink' &&
      ownershipRoutingResult
    ) {
      return getOwnershipReservationUsers({
        ownershipRoutingResult,
        additionalAttendees,
        meetingLimit,
      })
    }

    if (
      meetingAttempt.attempt.productFeature.type === 'GroupSchedulingLink' &&
      groupSchedulingLink
    ) {
      const hostId = meetingAttempt.attempt.productFeature.hostId ?? groupSchedulingLink.host.userId

      return {
        type: 'UsersWithLimit',
        limit: meetingLimit,
        userIds: getUniqUserIdsList([hostId, ...additionalAttendees]),
      }
    }
  }
}

const getRoundRobinReservationUsers = ({
  roundRobinRoutingResult,
  additionalAttendees = [],
}: {
  roundRobinRoutingResult: WithDistribution
  additionalAttendees?: string[]
}): ReservationUsers | undefined => {
  // Check if there is some flexible distribution with multiple users
  const hasFlexibleDistributionAssignments = roundRobinRoutingResult.distributions.some(
    distribution => isFlexibleDistribution(distribution) && (distribution.users?.length ?? 0) > 1
  )

  if (hasFlexibleDistributionAssignments) {
    return
  }

  const users = roundRobinRoutingResult.distributions
    .map(distribution =>
      distribution.users?.length
        ? {
            userId: distribution.users[0],
            distributionId: distribution.id,
          }
        : undefined
    )
    .filter(isDefined)

  return {
    type: 'DistributionUsers',
    additionalAttendees,
    attendeesFromDistribution: users,
  }
}

const getHandoffReservationUsers = ({
  routing,
  additionalAttendees = [],
  meetingLimit,
}: {
  routing?: SelectRouteResponse
  additionalAttendees?: string[]
  meetingLimit: MeetingLimitSource
}): ReservationUsers | undefined => {
  const distributionAssignments = routing?.selectedAssignees.filter(
    assignee => assignee.type === 'FromDistribution'
  )

  const hasFlexibleDistributionAssignments = distributionAssignments?.some(
    assignment => assignment.algorithm !== 'Strict' && assignment.users.length > 1
  )

  const usersFromDistribution =
    routing?.selectedAssignees
      .filter(assignment => assignment.type === 'UserFromDistribution')
      .map(assignment => ({
        userId: assignment.user,
        distributionId: assignment.distributionId,
      })) || []

  const distributionUserIdAssignments = !hasFlexibleDistributionAssignments
    ? distributionAssignments
        ?.map(assignment => {
          return {
            userId: assignment.users[0],
            distributionId: assignment.distributionId,
          }
        })
        .flat()
    : []

  const userIds =
    routing?.selectedAssignees
      .filter(assignment => assignment.type === 'User')
      .map(assignment => assignment.id) || []

  if (distributionUserIdAssignments?.length || usersFromDistribution?.length) {
    return {
      type: 'DistributionUsers',
      additionalAttendees: [...additionalAttendees, ...userIds],
      attendeesFromDistribution: [
        ...(distributionUserIdAssignments ?? []),
        ...usersFromDistribution,
      ],
    }
  }

  if (!hasFlexibleDistributionAssignments) {
    return {
      type: 'UsersWithLimit',
      limit: meetingLimit,
      userIds: R.unique(userIds),
    }
  }
}

const getOwnershipReservationUsers = ({
  ownershipRoutingResult,
  additionalAttendees = [],
  meetingLimit,
}: {
  additionalAttendees?: string[]
  ownershipRoutingResult: OwnershipLinkRoutingResult
  meetingLimit: MeetingLimitSource
}): ReservationUsers | undefined => {
  // Handle WithDistribution type (same as RoundRobin)
  if (isWithDistribution(ownershipRoutingResult)) {
    if (
      ownershipRoutingResult.distributions.length > 0 &&
      ownershipRoutingResult.distributions.every(
        distribution => isStrictDistribution(distribution) || distribution.users?.length === 1
      )
    ) {
      const users = ownershipRoutingResult.distributions
        .map(distribution =>
          distribution.users?.length
            ? {
                userId: distribution.users[0],
                distributionId: distribution.id,
              }
            : undefined
        )
        .filter(isDefined)

      return {
        type: 'DistributionUsers',
        additionalAttendees,
        attendeesFromDistribution: users,
      }
    }
  }

  // Handle MixedResults type
  if (isMixedResults(ownershipRoutingResult) && ownershipRoutingResult.result.length > 0) {
    const attendeesFromDistribution: AttendeeFromDistribution[] = []
    const owners: string[] = []

    for (const item of ownershipRoutingResult.result) {
      if (isOwner(item)) {
        owners.push(item.ownerId)
      } else if (isDistributionUsers(item)) {
        // For strict distributions or distributions with single user
        if (
          (isStrictDistribution(item) || item.users?.length === 1) &&
          item.users &&
          item.users.length > 0
        ) {
          attendeesFromDistribution.push({
            userId: item.users[0],
            distributionId: item.id,
          })
        } else {
          // Can't handle flexible distributions with multiple users
          return undefined
        }
      }
    }

    // Ensure we captured assignees from all items
    if ([...attendeesFromDistribution, ...owners].length !== ownershipRoutingResult.result.length) {
      return undefined
    }

    if (attendeesFromDistribution.length > 0) {
      return {
        type: 'DistributionUsers',
        additionalAttendees: additionalAttendees.concat(owners),
        attendeesFromDistribution,
      }
    }
  }

  // Handle OwnerFromSimpleRule type
  if (isOwnerFromSimpleRule(ownershipRoutingResult)) {
    if (ownershipRoutingResult.ownerId) {
      return {
        type: 'UsersWithLimit',
        limit: meetingLimit,
        userIds: getUniqUserIdsList([ownershipRoutingResult.ownerId, ...additionalAttendees]),
      }
    }
  }

  return undefined
}
